import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { authApi } from "../lib/authApi";
import { useAuth } from "../lib/authContext";

/**
 * One-shot handler for the Emergent OAuth callback.
 * Reads `#session_id=` from the URL fragment, exchanges it for our JWT
 * cookies via /api/auth/google/exchange, refreshes AuthContext, then
 * navigates to the home page (or wherever `?next=` points to).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const fragment = (location.hash || window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");
    const nextParam = new URLSearchParams(location.search).get("next");

    if (!sessionId) {
      toast.error("Sessão Google ausente. Tente novamente.");
      navigate("/entrar", { replace: true });
      return;
    }

    (async () => {
      try {
        await authApi.googleExchange({ session_id: sessionId });
        const me = await refresh();
        toast.success("Bem-vindo!");
        const isAdmin = me?.role === "admin";
        const needsOnboarding = !isAdmin && !me?.spiritual_profile;
        // FASE 2 — admin não é enviado ao painel após login.
        let target = nextParam || "/";
        if (needsOnboarding && !nextParam) {
          target = "/bem-vindo?next=%2F";
        }
        navigate(target, { replace: true });
      } catch (err) {
        toast.error("Não foi possível concluir o login com Google.");
        navigate("/entrar", { replace: true });
      }
    })();
  }, [location.hash, location.search, navigate, refresh]);

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center section-padding"
      data-testid="auth-callback"
    >
      <p className="text-navy/55 text-sm uppercase tracking-widest">
        Concluindo entrada com Google…
      </p>
    </div>
  );
}
